import { __decorate, __rest } from "tslib";
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ComponentUtil, Grid, ModuleRegistry } from '@ag-grid-community/core';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { Session } from '@genesislcap/foundation-comms';
import { insertDocumentCSSRule, LifecycleMixin, respondToVisibility, } from '@genesislcap/foundation-utils';
import { attr, DOM, observable } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { classNames } from '@microsoft/fast-web-utilities';
import { GridProCell } from './cell';
import { SelectEditor, NumberEditor, MultiselectEditor, DateEditor, StringEditor, } from './cell-editors';
import { ActionRenderer, ActionsMenuRenderer, BooleanRenderer, EditableRenderer, } from './cell-renderers';
import { GridProColumn } from './column';
import { GridProClientSideDatasource, GridProServerSideDatasource } from './datasource';
import { agThemeFontFaceMap, defaultAgGridFontFace } from './external';
import { GridProGenesisDatasource } from './grid-pro-genesis-datasource';
import { DEBOUNCED_RESIZE_TIME } from './grid-pro.definitions';
import { foundationGridProStyles as styles } from './grid-pro.styles';
import { gridProTemplate as template } from './grid-pro.template';
import { AgGridTheme, GridProRendererTypes, agThemeFontsId, agThemeTokenMapClassname, GRID_READY_EVENT, ERROR_COLUMN_FIELD, EDITED_COLUMN_FIELD, GridOptionsConfig, } from './grid-pro.types';
import { ErrorTooltip } from './tooltips';
import { logger, mergeAndDedupColDefWithColumnState } from './utils';
GridProGenesisDatasource;
GridProClientSideDatasource;
GridProServerSideDatasource;
GridProCell;
GridProColumn;
ModuleRegistry.registerModules([CsvExportModule, ClientSideRowModelModule]);
/**
 * Convert string values from 'camelCase' to 'kebab-case'
 * @param value - The value to convert to Kebab case.
 * @returns The Kebab case result.
 * @public
 */
export const convertToKebabCase = (value) => value.replace(/([a-z])([A-Z])|\s+/g, '$1-$2').toLowerCase();
/**
 * The Grid Pro element.
 * @remarks Grid Pro is a Web Component wrapper around the AG Grid Community library.
 * @public
 * @tagname %%prefix%%-grid-pro
 */
export class GridPro extends LifecycleMixin(FoundationElement) {
    themeChanged(oldValue, newValue) {
        if (!newValue) {
            this.classNames = '';
            this.destroyFontFace();
            return;
        }
        this.classNames = classNames(agThemeTokenMapClassname(newValue), newValue);
        this.insertFontFace(newValue);
    }
    constructor() {
        super();
        // Genesis-specific attrs
        this.autoCellRendererByType = false;
        this.onlyTemplateColDefs = false;
        // AG-specific attrs
        /**
         * Boolean attribute to control whether the grid autosizes the columns upon interaction.
         * This will disable the column widths from being manually set, and doesn't save the widths
         * in local storage if you are using `persist-column-state-key`.
         */
        this.gridAutosizingEnabled = false;
        /**
         * The index to add new rows to when using `applyTransaction` or `applyTransactionAsync`
         */
        this.addIndex = 0;
        /**
         * Whether to use the `applyTransactionAsync` function for *add* transactions
         * @remarks Defaults to false
         */
        this.asyncAdd = false;
        /**
         * Whether to use the `applyTransactionAsync` function for *remove* transactions
         * @remarks Defaults to false
         */
        this.asyncRemove = false;
        /**
         * Whether to use the `applyTransactionAsync` function for *update* transactions
         * @remarks Defaults to true
         */
        this.asyncUpdate = true;
        /**
         * If false, will disable cell flashing for all cells by default, unless otherwise defined in custom colDef
         * @remarks Defaults to true
         */
        this.enableCellFlashing = false;
        /**
         * @deprecated - use `enableRowFlashing` instead.
         */
        this.enabledRowFlashing = false;
        /**
         * If true, will enable row flashing for all rows for `add` async transactions
         */
        this.enableRowFlashing = false;
        this.gridFontFace = defaultAgGridFontFace;
        this.columnComponentName = 'grid-pro-column';
        this.theme = AgGridTheme.alpine;
        this.rehydrationAttempted = false;
        this.filterConfig = undefined;
        this.agAttributes = {};
        this.agPropertiesMap = ComponentUtil.ALL_PROPERTIES.reduce((map, property) => {
            map[property.toLowerCase()] = property;
            map[convertToKebabCase(property)] = property;
            return map;
        }, {});
    }
    connectedCallback() {
        super.connectedCallback();
        this.rehydrationAttempted = false;
        if (this.gridAutosizingEnabled) {
            respondToVisibility(this, (gridVisible) => {
                if (gridVisible) {
                    this.debouncedColumnAutosize.bind(this)();
                }
            });
        }
        if (!this.shouldRunConnect)
            return;
        ComponentUtil.ALL_PROPERTIES.forEach((key) => {
            Object.defineProperty(this, key, {
                set: (v) => {
                    this.agAttributeChangedCallback(convertToKebabCase(key), undefined, v);
                },
                enumerable: true,
                configurable: true,
            });
        });
    }
    disconnectedCallback() {
        var _a;
        this.cacheFilterConfig();
        super.disconnectedCallback();
        this.rehydrationAttempted = false;
        if (!this.shouldRunDisconnect)
            return;
        this.initialised = false;
        (_a = this.gridApi) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    combineAllGridComponents(gridOptionsComponents) {
        const defaultFoundationAgComponents = {
            [GridProRendererTypes.action]: ActionRenderer,
            [GridProRendererTypes.actionsMenu]: ActionsMenuRenderer,
            [GridProRendererTypes.boolean]: BooleanRenderer,
            [GridProRendererTypes.editable]: EditableRenderer,
            [GridProRendererTypes.selectEditor]: SelectEditor,
            [GridProRendererTypes.numberEditor]: NumberEditor,
            [GridProRendererTypes.multiselectEditor]: MultiselectEditor,
            [GridProRendererTypes.dateEditor]: DateEditor,
            [GridProRendererTypes.stringEditor]: StringEditor,
        };
        return Object.assign(Object.assign(Object.assign({}, defaultFoundationAgComponents), gridOptionsComponents), this.gridComponents);
    }
    statePersistanceEnabled() {
        if (!this.persistColumnStateKey || this.persistColumnStateKey.length === 0) {
            return false;
        }
        return true;
    }
    /**
     * Because we try and resize the grid to fit using callbacks on events, this will cause
     * a stack overflow of calls to resize, so we need to debounce it.
     * @internal
     */
    debouncedColumnAutosize() {
        if (!this.gridAutosizingEnabled)
            return;
        if (this.debounced) {
            clearTimeout(this.debounced);
            this.debounced = null;
        }
        this.debounced = setTimeout(() => {
            var _a;
            (_a = this.gridApi) === null || _a === void 0 ? void 0 : _a.sizeColumnsToFit();
        }, DEBOUNCED_RESIZE_TIME);
    }
    /**
     * If {@link @genesislcap/grid-pro#GridPro.gridAutosizingEnabled} is true, will remove the widths from the column states.
     * @internal
     */
    removeConfigWidthsToAutosize(colState) {
        return colState.map((col) => (Object.assign(Object.assign({}, col), { width: null })));
    }
    saveColumnState() {
        if (this.rehydrationAttempted && this.statePersistanceEnabled()) {
            const existingOptions = JSON.parse(this.session.getLocalStorageItem('gridOptions') || '{}');
            let colState = this.columnApi.getColumnState();
            colState = this.gridAutosizingEnabled
                ? this.removeConfigWidthsToAutosize(colState)
                : colState;
            if (colState.length === 0)
                return;
            existingOptions[this.persistColumnStateKey] = JSON.stringify(colState);
            this.session.setLocalStorageItem('gridOptions', JSON.stringify(existingOptions));
        }
    }
    /**
     * Gets the saved grid ColumnState[] from local storage
     * @remarks This is used to restore the grid state when the grid is rehydrated
     * @privateRemarks Not using `columnApi.get|setColumnState()` for these flows as it doesn't work setting new columnDefs vs different ordering.
     * @public
     */
    getSavedColumnState() {
        const localStorageGridOptions = this.session.getLocalStorageItem('gridOptions');
        if (this.statePersistanceEnabled() && localStorageGridOptions) {
            const allGridOptions = JSON.parse(localStorageGridOptions);
            if (this.persistColumnStateKey in allGridOptions) {
                return JSON.parse(allGridOptions[this.persistColumnStateKey]);
            }
        }
        return [];
    }
    restoreColumnState() {
        var _a;
        this.rehydrationAttempted = true;
        const colState = this.getSavedColumnState();
        if (colState && colState.length > 0) {
            (_a = this.columnApi) === null || _a === void 0 ? void 0 : _a.applyColumnState({
                state: colState,
                applyOrder: true,
            });
        }
    }
    cacheFilterConfig() {
        var _a;
        this.filterConfig = ((_a = this.gridApi) === null || _a === void 0 ? void 0 : _a.getFilterModel()) || undefined;
    }
    restoreCachedFilterConfig() {
        var _a, _b;
        if (typeof this.filterConfig !== 'undefined') {
            (_a = this.gridApi) === null || _a === void 0 ? void 0 : _a.setFilterModel(this.filterConfig);
            (_b = this.gridApi) === null || _b === void 0 ? void 0 : _b.onFilterChanged();
        }
    }
    /**
     * @public
     */
    get gridOptions() {
        return this.agGridOptions;
    }
    get gridProDatasource() {
        return (this.querySelector('grid-pro-genesis-datasource') ||
            this.querySelector('grid-pro-client-side-datasource') ||
            this.querySelector('grid-pro-server-side-datasource'));
    }
    set gridOptions(options) {
        const globalEventListener = this.globalEventListener.bind(this);
        const gridOnChangeCallback = () => {
            this.saveColumnState.bind(this)();
            this.debouncedColumnAutosize.bind(this)();
        };
        Array.from(this.attributes).forEach((attribute) => {
            const attrName = this.agPropertiesMap[attribute.name];
            if (!attrName)
                return;
            this.agAttributes[attrName] = attribute.value;
        });
        const { columnDefs, components, defaultColDef, onGridReady, onFirstDataRendered } = options, rest = __rest(options, ["columnDefs", "components", "defaultColDef", "onGridReady", "onFirstDataRendered"]);
        const derivedOptions = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, this.gridOptionsConfig), { defaultColDef: Object.assign({ enableCellChangeFlash: this.enableCellFlashing, filter: true, resizable: true, sortable: true }, defaultColDef), components: this.combineAllGridComponents(components), suppressDragLeaveHidesColumns: true }), this.eventsAndCallbacks), { onGridReady: (event) => {
                var _a, _b;
                this.gridApi = (_a = options.api) !== null && _a !== void 0 ? _a : event.api;
                this.columnApi = (_b = options.columnApi) !== null && _b !== void 0 ? _b : event.columnApi;
                if (onGridReady) {
                    onGridReady(event);
                }
                this.$emit(GRID_READY_EVENT, {
                    event,
                    gridId: this.id,
                });
                this.restoreColumnState.bind(this)();
                this.debouncedColumnAutosize.bind(this)();
            }, onFirstDataRendered: (event) => {
                this.restoreCachedFilterConfig.bind(this)();
                if (onFirstDataRendered) {
                    onFirstDataRendered(event);
                }
            }, onColumnPinned: gridOnChangeCallback, onColumnResized: gridOnChangeCallback, onColumnMoved: gridOnChangeCallback, onDisplayedColumnsChanged: gridOnChangeCallback, onFilterChanged: gridOnChangeCallback, onGridSizeChanged: gridOnChangeCallback, onSortChanged: gridOnChangeCallback }), rest);
        if (this.gridProDatasource) {
            derivedOptions.columnDefs = columnDefs;
        }
        else {
            derivedOptions.columnDefs = this.mergeAllColumnDefsAndStates(columnDefs);
        }
        this.agGridOptions = Object.assign(Object.assign({}, this.agGridOptions), ComponentUtil.copyAttributesToGridOptions(derivedOptions, this.agAttributes));
        const gridParams = {
            globalEventListener: globalEventListener,
        };
        DOM.queueUpdate(() => {
            if (this.initialised) {
                // Needed due gridSlot children cleanup
                if (this.gridSlot.firstChild) {
                    // prevent re-instantiating the grid using the same slot content
                    this.gridSlot.removeChild(this.gridSlot.firstChild);
                }
            }
            this.agGrid = new Grid(this.gridSlot, this.agGridOptions, gridParams);
            this.initialised = true;
        });
    }
    /**
     * Will merge templated column definitions with `columnDefs` plus localStorage's column state or `deferredColumnStates`.
     * @deprecated use `applyTemplateDefinitions` instead.
     * @param columnDefs - The column definitions to apply to the grid.
     * @param deferredColumnDefsOrState - If true, will merge the column definitions with the saved column state or the `deferredColumnStates` property on the `grid-pro-genesis-datasource` element.
     * @returns The merged column definitions.
     */
    applyTemplateDefinitions(columnDefs, deferredColumnDefsOrState = false) {
        logger.deprecated('applyTemplateDefinitions', 'Use `mergeAllColumnDefsAndStates` instead.');
        return this.mergeAllColumnDefsAndStates(columnDefs, deferredColumnDefsOrState);
    }
    /**
     * Will merge templated column definitions with `columnDefs` plus localStorage's column state or `deferredColumnStates`.
     * @public
     * @remarks This will favor the column state from the browser's local storage over the `deferredColumnStates` property on the `grid-pro-genesis-datasource` element.
     * @param columnDefs - The column definitions to apply to the grid.
     * @param deferredColumnDefsOrState - If true, will merge the column definitions with the saved column state or the `deferredColumnStates` property
     * on the `grid-pro-genesis-datasource` element. Defaults to false but `grid-pro-genesis-datasource` will set this to true.
     * @returns The merged column definitions.
     */
    mergeAllColumnDefsAndStates(columnDefs, deferredColumnDefsOrState = false) {
        var _a;
        const columnDefinitionsFromMetadata = columnDefs || [];
        const templateColumnDefs = Array.from(this.querySelectorAll(this.columnComponentName))
            .map((templateColumn) => templateColumn.definition)
            .map((columnDef) => {
            // enable cell flashing for each column if unspecified on column level but enabled on grid level
            if (this.enableCellFlashing && columnDef.enableCellChangeFlash === undefined) {
                columnDef.enableCellChangeFlash = true;
            }
            if (columnDef.editable) {
                if (!columnDef.cellRenderer && !columnDef.cellRendererSelector) {
                    columnDef.cellRendererSelector = (params) => {
                        if (params.data[EDITED_COLUMN_FIELD] === columnDef.field) {
                            return {
                                component: GridProRendererTypes.editable,
                            };
                        }
                    };
                }
                if (!columnDef.tooltipComponent && !columnDef.tooltipValueGetter) {
                    columnDef.tooltipComponent = ErrorTooltip;
                    columnDef.tooltipValueGetter = (params) => {
                        const error = params.data[ERROR_COLUMN_FIELD];
                        if (error && error.column === columnDef.field) {
                            return error.message;
                        }
                    };
                }
                if (!columnDef.cellClassRules) {
                    columnDef.cellClassRules = {
                        error: (params) => { var _a; return ((_a = params.data[ERROR_COLUMN_FIELD]) === null || _a === void 0 ? void 0 : _a.column) === params.colDef.field; },
                    };
                }
            }
            const metadataColumnDef = columnDefinitionsFromMetadata.find((colDef) => columnDef.field === colDef.field);
            return Object.assign(Object.assign({}, metadataColumnDef), columnDef);
        });
        const templateColumnKeys = templateColumnDefs.map((x) => x.field);
        const colDefsToReturn = [
            ...templateColumnDefs,
            ...(this.onlyTemplateColDefs
                ? []
                : columnDefinitionsFromMetadata.filter((g) => !templateColumnKeys.includes(g.field))),
        ];
        if (this.gridProDatasource && deferredColumnDefsOrState) {
            const savedColumnState = this.getSavedColumnState();
            if ((savedColumnState === null || savedColumnState === void 0 ? void 0 : savedColumnState.length) > 0) {
                return mergeAndDedupColDefWithColumnState(colDefsToReturn, savedColumnState);
            }
            else if (((_a = this.gridProDatasource.deferredColumnStates) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                return mergeAndDedupColDefWithColumnState(colDefsToReturn, this.gridProDatasource.deferredColumnStates);
            }
        }
        return colDefsToReturn;
    }
    get observedAttributes() {
        // allow properties to be supplied either lowercased or kebab-cased
        // this allows the user to either supply (for example) enableSorting or enabled-sorting
        // properties lowercased
        const lowerCasedPropertyNames = ComponentUtil.ALL_PROPERTIES.map((property) => property.toLowerCase());
        // properties kebab-cased
        const kebabCasePropertyNames = ComponentUtil.ALL_PROPERTIES.map((property) => convertToKebabCase(property));
        return lowerCasedPropertyNames.concat(kebabCasePropertyNames);
    }
    agAttributeChangedCallback(attName, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }
        const gridPropertyName = this.agPropertiesMap[attName];
        if (gridPropertyName) {
            // for properties set before gridOptions is called
            this.agAttributes[gridPropertyName] = newValue;
            if (this.initialised) {
                // for subsequent (post gridOptions) changes
                const changeObject = {};
                changeObject[gridPropertyName] = { currentValue: newValue };
                ComponentUtil.processOnChange(changeObject, this.gridApi);
            }
        }
    }
    globalEventListener(eventType, event) {
        const eventLowerCase = eventType.toLowerCase();
        const browserEvent = new Event(eventLowerCase);
        const browserEventNoType = browserEvent;
        browserEventNoType.agGridDetails = event;
        // for when defining events via myGrid.addEventListener('columnresized', function (event) {...
        this.dispatchEvent(browserEvent);
        // for when defining events via myGrid.oncolumnresized = function (event) {....
        const callbackMethod = 'on' + eventLowerCase;
        if (typeof this[callbackMethod] === 'function') {
            this[callbackMethod](browserEvent);
        }
    }
    insertFontFace(newValue) {
        const fontFaceRule = agThemeFontFaceMap[newValue] || this.gridFontFace;
        this.destroyFontFace();
        const newDestroy = insertDocumentCSSRule(fontFaceRule, agThemeFontsId(newValue));
        if (newDestroy) {
            this.fontFaceDestroy = newDestroy;
        }
    }
    destroyFontFace() {
        if (this.fontFaceDestroy) {
            this.fontFaceDestroy();
            this.fontFaceDestroy = null;
        }
    }
}
__decorate([
    observable
], GridPro.prototype, "columnApi", void 0);
__decorate([
    observable
], GridPro.prototype, "gridApi", void 0);
__decorate([
    Session
], GridPro.prototype, "session", void 0);
__decorate([
    attr({ attribute: 'auto-cell-renderer-by-type', mode: 'boolean' })
], GridPro.prototype, "autoCellRendererByType", void 0);
__decorate([
    attr({ attribute: 'only-template-col-defs', mode: 'boolean' })
], GridPro.prototype, "onlyTemplateColDefs", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'grid-autosizing' })
], GridPro.prototype, "gridAutosizingEnabled", void 0);
__decorate([
    attr({ attribute: 'add-index' })
], GridPro.prototype, "addIndex", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-add' })
], GridPro.prototype, "asyncAdd", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-remove' })
], GridPro.prototype, "asyncRemove", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-update' })
], GridPro.prototype, "asyncUpdate", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-cell-flashing' })
], GridPro.prototype, "enableCellFlashing", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enabled-row-flashing' })
], GridPro.prototype, "enabledRowFlashing", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-row-flashing' })
], GridPro.prototype, "enableRowFlashing", void 0);
__decorate([
    attr({ attribute: 'persist-column-state-key' })
], GridPro.prototype, "persistColumnStateKey", void 0);
__decorate([
    observable
], GridPro.prototype, "gridFontFace", void 0);
__decorate([
    attr
], GridPro.prototype, "columnComponentName", void 0);
__decorate([
    observable
], GridPro.prototype, "eventsAndCallbacks", void 0);
__decorate([
    attr
], GridPro.prototype, "headerHeight", void 0);
__decorate([
    attr
], GridPro.prototype, "rowHeight", void 0);
__decorate([
    attr
], GridPro.prototype, "theme", void 0);
__decorate([
    observable
], GridPro.prototype, "classNames", void 0);
__decorate([
    observable
], GridPro.prototype, "agGrid", void 0);
__decorate([
    observable
], GridPro.prototype, "gridSlot", void 0);
__decorate([
    observable
], GridPro.prototype, "gridComponents", void 0);
__decorate([
    GridOptionsConfig
], GridPro.prototype, "gridOptionsConfig", void 0);
/**
 * GridPro shadow root options.
 * @public
 * @remarks
 * For extensibility, this can be used to set the shadow root mode and delegatesFocus
 */
export const foundationGridProShadowOptions = undefined;
/**
 * Grid Pro default configuration.
 * @public
 */
export const defaultGridProConfig = {
    shadowOptions: undefined,
};
/**
 * A function that returns a Foundation Grid Pro registration for configuring the component with a DesignSystem.
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-grid-pro\>
 */
export const foundationGridPro = GridPro.compose(Object.assign({ baseName: 'grid-pro', styles,
    template, shadowOptions: foundationGridProShadowOptions }, defaultGridProConfig));
